import { allPages } from "@nexxt/app-kit";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { catalogClient, type LinesheetLine } from "../client";

// Server state of the lines of a linesheet (pattern.md §10). Their keys are under the linesheet's, so whatever
// reloads a linesheet reloads its lines too.

export type { LineOptions, LinesheetLine } from "../client";
/** How a line is named where a row has to be told from the others: its style code, or its position until it has one. */
export const lineName = (line: LinesheetLine): string => line.product.styleCode ?? String(line.position);

/** Which lines the grid shows: all of them, or the ones that need attention. */
export const lineViews = ["all", "issues", "new", "deleteRequested"] as const;
export type LineView = (typeof lineViews)[number];

export interface LineFilters {
  view?: LineView | undefined;
  q?: string | undefined;
}

const viewQuery: Record<LineView, object> = {
  all: {},
  issues: { onlyIssues: true },
  new: { onlyNew: true },
  deleteRequested: { onlyDeleteRequested: true },
};

/** Every line of a linesheet: what an export writes. */
export const allLines = (linesheetId: string): Promise<LinesheetLine[]> =>
  allPages<LinesheetLine>((cursor) =>
    catalogClient().listLinesheetLines({ linesheetId, ...(cursor && { cursor }), limit: 500 }),
  );

const keys = {
  /** Every cached list of lines of a linesheet: the mutations reload them all. */
  ofLinesheet: (linesheetId: string) => ["linesheets", "lines", linesheetId] as const,
};

export const lineQueries = {
  ...keys,

  list: (linesheetId: string, { view = "all", q }: LineFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [...keys.ofLinesheet(linesheetId), { view, q }],
      queryFn: ({ pageParam, signal }) =>
        catalogClient().listLinesheetLines(
          { linesheetId, ...viewQuery[view], ...(q && { q }), ...(pageParam && { cursor: pageParam }), limit: 100 },
          signal,
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** The values of the line form (ADR-048): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: ["linesheets", "line-options"],
      queryFn: ({ signal }) => catalogClient().getLineOptions({}, signal),
      staleTime: 10 * 60_000,
    }),

  /** Products of the brand to carry over (FR-M2-03): the first page of the search is enough to pick from. */
  carryoverCandidates: (linesheetId: string, q: string) =>
    queryOptions({
      queryKey: [...keys.ofLinesheet(linesheetId), "carryover-candidates", q],
      queryFn: ({ signal }) =>
        catalogClient().listCarryoverCandidates({ linesheetId, ...(q && { q }), limit: 50 }, signal),
    }),
};
